import { createSelector } from '@reduxjs/toolkit'

import { BillingCurrency } from 'common/billing/constants/BillingCurrency'
import { AddOnStatus, AddOnSlug } from 'common/billing/entities/addOns/interfaces'
import { getRootBillingState } from 'common/billing/entities/miscellaneous/selectors'
import { ProInfo } from 'common/billing/entities/pro/interfaces'
import { getAccountFlags, getCurrentAccount } from 'common/core/selectors/appSelectors'
import { AddOn, Price } from 'shared/api/requests/billing/schemas'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'

/**
 * Local usage from pro selectors to handle cycle deps.
 */
const getBillingProInfo = (state: RootState): ProInfo => state.billing.pro

const getProProducts = createSelector(getBillingProInfo, ({ products }) => {
  if (!products) {
    return null
  }

  return products
})

export const getAddOnsData = createSelector(
  [getBillingProInfo],
  ({ addons_data }): AddOn[] => addons_data || [],
)

export const getCancelAddOnModalRoot = createSelector(
  [getRootBillingState],
  (billing) => billing.cancelAddOnModal,
)

export const getCancelAddOnModalState = createSelector(
  [getCancelAddOnModalRoot, getAddOnsData],
  (cancelAddOnModal, addOns) => {
    const addOn = addOns.find((addOn) => addOn.slug === cancelAddOnModal.addOnSlug)

    if (cancelAddOnModal.isOpen && addOn) {
      return {
        isOpen: true,
        loading: cancelAddOnModal.loading,
        addOn,
        isInterviewModalOpen: cancelAddOnModal.isInterviewModalOpen,
      } as const
    }

    return {
      isOpen: false,
      loading: cancelAddOnModal.loading,
      addOn: undefined,
      isInterviewModalOpen: cancelAddOnModal.isInterviewModalOpen,
    } as const
  },
)

export const getActiveAddOns = createSelector([getAddOnsData], (addOns): AddOnSlug[] =>
  addOns.reduce<AddOnSlug[]>((slugs, addOn) => {
    if (addOn.status === AddOnStatus.ACTIVE) {
      slugs.push(addOn.slug)
    }
    return slugs
  }, []),
)

interface InboxData {
  isAvailable: boolean
  isEnabled: boolean
  isAddOn: boolean
  price: Price
  seats: {
    current: number
    limit: number
  }
}

export const getInboxData = createSelector(
  getAddOnsData,
  getAccountFlags,
  getProProducts,
  getCurrentAccount,
  (addOns, flags, products, { billing_info }): InboxData => {
    const currency = billing_info?.currency ?? BillingCurrency.USD

    if (!flags?.[AccountFlag.ENABLE_INBOX] && !flags?.[AccountFlag.ENABLE_INBOX_ADDON]) {
      return {
        isAvailable: false,
        isEnabled: false,
        isAddOn: true,
        price: {
          amount: 0,
          currency,
        },
        seats: {
          current: 0,
          limit: -1,
        },
      }
    }

    const inboxAddOn = addOns.find((addOn) => addOn.slug === AddOnSlug.INBOX)
    if (inboxAddOn) {
      return {
        isAvailable: true,
        isEnabled: inboxAddOn.status === AddOnStatus.ACTIVE,
        isAddOn: true,
        price: {
          amount: inboxAddOn.price,
          currency,
        },
        seats: {
          current: inboxAddOn.count,
          limit: inboxAddOn.limit,
        },
      }
    }

    return {
      isAvailable: true,
      isEnabled: products?.inbox?.active ?? false,
      isAddOn: false,
      price: {
        amount: products?.inbox?.price ?? 0,
        currency,
      },
      seats: {
        current: products?.inbox?.current_paid_users_count ?? 0,
        limit: products?.inbox?.total_paid_users_count ?? 0,
      },
    }
  },
)
